import {
    type MutationFunctionContext,
    type UseMutationOptions,
    useMutation,
    useQueryClient,
} from "@tanstack/react-query";
import { api } from "api";
import type { PatientManualProvisionRequest } from "api/requests";

export type PatientManualProvisionOptions = UseMutationOptions<number, Error, PatientManualProvisionRequest>;

/**
 * Client hook to provision a P/RP.
 * @param options Options for the client call; provides callbacks for when the API call succeeds or fails.
 * @returns React Query mutation properties.
 */
export function useProvisionPatientManually(options: Omit<PatientManualProvisionOptions, "mutationFn">) {
    const queryClient = useQueryClient();

    /** Provide built-in onSuccess behavior, then call provided callback if available. */
    const onSuccessWrapper = (
        data: number,
        variables: PatientManualProvisionRequest,
        onMutateResult: unknown,
        context: MutationFunctionContext,
    ) => {
        // Invalidate queries that are affected by posting a transaction
        void queryClient.invalidateQueries({ queryKey: ["searchPatientField"] });
        void queryClient.invalidateQueries({ queryKey: ["patientActivity"] });

        // Use callback if provided
        return options.onSuccess?.(data, variables, onMutateResult, context);
    };

    return useMutation({
        ...options,
        mutationFn: async (request: PatientManualProvisionRequest) => await api.provider.provisionManually(request),
        onSuccess: onSuccessWrapper,
    });
}
